import { IUser } from '@vivli/shared/infrastructure/interface';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { first } from 'rxjs/operators';
import ReactGA from 'react-ga4';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { IUsersApi } from '@vivli/features/users/infrastructure/interface';
import { IActiveUser } from '@vivli/core/infrastructure/interface';
import { Subscription } from 'rxjs';
import { useAppConfigContext, useAuthEventsContext, useAuthServiceContext } from '@vivli/core/infrastructure/context';
import { LoggerService, maskEmail } from '@vivli/shared/infrastructure/service';

const getClaimsFromIdToken = (idToken: string): any => {
    if (!idToken) {
        LoggerService.warn('[Auth:User] getClaimsFromIdToken: no id token supplied, no claims to read');
        return null;
    }

    try {
        return JSON.parse(atob(idToken.split('.')[1]));
    } catch (error) {
        LoggerService.error('[Auth:User] getClaimsFromIdToken: failed to decode the id token payload', error);
        return null;
    }
};

interface IUserContext {
    usersApi: IUsersApi;
}

export const useActiveUser = ({ usersApi }: IUserContext): IActiveUser => {
    const config = useAppConfigContext();
    const { getTokenSilent } = useAuthServiceContext();
    const { getUserOnLogin } = usersApi;
    const authEvents = useAuthEventsContext();

    const [user, setUser] = useState<IUser>(null);
    const authSub = useRef<Subscription>();
    const setupUserInProgressRef = useRef(false);

    const userHasAnyPermission = useCallback((userIn: IUser, permissions: PermissionsEnum[]) => {
        return (
            (userIn?.vivliPermissions?.some((p) => permissions?.includes(p)) ||
                userIn?.orgMemberships?.some((om) => om.isEnabled && om.vivliPermissions?.some((pr) => permissions?.includes(pr)))) ??
            false
        );
    }, []);

    const assignUserParams = useCallback(
        (userToAssign: IUser) => {
            //TODO PERMFE - redo with permissions+
            // userToAssign.isAnnotator = userToAssign?.orgMemberships?.some((uom) => om.isAnnotator);
            // userToAssign.isQaReviewer = userToAssign?.orgMemberships?.some((om) => om.isQaReviewer);
            userToAssign.isAnnotator = userHasAnyPermission(userToAssign, [PermissionsEnum.CurateStudy]);
            userToAssign.isQaReviewer = userHasAnyPermission(userToAssign, [PermissionsEnum.QaCuration]);
        },
        [userHasAnyPermission]
    );

    const userHasPermission = useCallback((userIn: IUser, permission: PermissionsEnum) => {
        return userIn?.vivliPermissions?.includes(permission) ?? false;
    }, []);

    const hasPermission = useCallback(
        (permission: PermissionsEnum) => {
            return userHasPermission(user, permission);
        },
        [user, userHasPermission]
    );

    const userOrgHasPermission = useCallback((userIn: IUser, orgId: string, permission: PermissionsEnum) => {
        return (
            userIn?.orgMemberships?.some((org) => org.isEnabled && org.orgId == orgId && org.vivliPermissions.includes(permission)) ?? false
        );
    }, []);

    const orgHasPermission = useCallback(
        (orgId: string, permission: PermissionsEnum) => {
            return userOrgHasPermission(user, orgId, permission);
        },
        [user, userOrgHasPermission]
    );

    const hasAnyPermission = useCallback(
        (permissions: PermissionsEnum[] | PermissionsEnum) => {
            const _permissions = Array.isArray(permissions) ? permissions : [permissions];
            return userHasAnyPermission(user, _permissions);
        },
        [user, userHasAnyPermission]
    );

    const hasAllPermissions = useCallback(
        (permissions: PermissionsEnum[]) => {
            return permissions.every(hasAnyPermission);
        },
        [hasAnyPermission]
    );

    const userHasAnyPermissionForOrg = useCallback((userIn: IUser, orgId: string, permissions: PermissionsEnum[]) => {
        return (
            userIn?.orgMemberships?.some(
                (om) => om.isEnabled && om.orgId === orgId && om.vivliPermissions?.some((pr) => permissions?.includes(pr))
            ) ?? false
        );
    }, []);

    const hasAnyPermissionForOrg = useCallback(
        (orgId: string, permissions: PermissionsEnum[]) => {
            return userHasAnyPermissionForOrg(user, orgId, permissions);
        },
        [user, userHasAnyPermissionForOrg]
    );

    //special purpose where we don't care what the org is, just if user has some permission in SOME org
    const userHasAnyPermissionForAnyOrg = useCallback((userIn: IUser, permissions: PermissionsEnum[]) => {
        return userIn?.orgMemberships?.some((om) => om.isEnabled && om.vivliPermissions?.some((pr) => permissions?.includes(pr))) ?? false;
    }, []);

    const hasAnyPermissionForAnyOrg = useCallback(
        (permissions: PermissionsEnum[]) => {
            return userHasAnyPermissionForAnyOrg(user, permissions);
        },
        [user, userHasAnyPermissionForAnyOrg]
    );

    // Single exit point for every user-fetch failure. Clearing the in-progress guard here is what
    // keeps a failed fetch recoverable - leaving it set makes every later login-success no-op at the
    // guard check, so the user could never be hydrated again without a full page reload.
    const failUserInfo = useCallback(
        (message: string, error?: unknown) => {
            LoggerService.error(`[Auth:User] ${message}`, ...(error ? [error] : []));
            setupUserInProgressRef.current = false;
            authEvents.next('get-user-info-failure');
        },
        [authEvents]
    );

    const getUserWithRoles = useCallback(() => {
        if (setupUserInProgressRef.current) {
            LoggerService.warn('[Auth:User] getUserWithRoles: a user fetch is already in progress, skipping this request');
            return;
        }
        setupUserInProgressRef.current = true;

        LoggerService.debug('[Auth:User] getUserWithRoles: starting user hydration');
        authEvents.next('get-user-info');

        getTokenSilent()
            .then((tokenResponse) => {
                const claims = getClaimsFromIdToken(tokenResponse);
                if (!claims) {
                    failUserInfo('getUserWithRoles: no claims available from the id token, cannot load the user');
                    return;
                }

                const oidClaim = claims['oid'];
                const nameClaim = claims['name'];
                const emailClaim = claims['email'];
                const expClaim = claims['exp'];
                const idpClaim = claims['idp'];

                LoggerService.debug('[Auth:User] getUserWithRoles: id token claims parsed', {
                    oid: oidClaim,
                    email: maskEmail(emailClaim),
                    exp: expClaim,
                    idp: idpClaim
                });

                let user: IUser;
                getUserOnLogin(oidClaim)
                    .pipe(first())
                    .subscribe({
                        next: (response) => {
                            user = {
                                ...response,
                                userId: oidClaim,
                                email: emailClaim,
                                expiration: expClaim,
                                name: nameClaim
                            };

                            assignUserParams(user);

                            // always set user regardless of getting roles
                            setUser(user);
                            if (config.gaMeasurementId) {
                                ReactGA.set({ userId: user.userId });
                            }

                            LoggerService.debug('[Auth:User] getUserWithRoles: user hydrated', {
                                userId: user.userId,
                                email: maskEmail(user.email),
                                orgMemberships: user.orgMemberships?.length ?? 0,
                                vivliPermissions: user.vivliPermissions?.length ?? 0,
                                isAnnotator: user.isAnnotator,
                                isQaReviewer: user.isQaReviewer
                            });

                            setupUserInProgressRef.current = false;
                            authEvents.next('get-user-info-success');
                        },
                        error: (error) => {
                            failUserInfo('getUserWithRoles: getUserOnLogin failed', error);
                        }
                    });
            })
            .catch((error) => {
                // getTokenSilent swallows its own errors today, but a rejection here would otherwise
                // leave the in-progress guard stuck true and go completely unreported.
                failUserInfo('getUserWithRoles: getTokenSilent rejected', error);
            });
    }, [config.gaMeasurementId, getUserOnLogin, assignUserParams, failUserInfo]);

    const cleanupUser = useCallback(() => {
        LoggerService.debug('[Auth:User] cleanupUser: clearing the active user');
        setUser(null);
    }, []);

    useEffect(() => {
        if (authSub.current) {
            return;
        }

        authSub.current = authEvents.subscribe((event) => {
            switch (event) {
                case 'login-success':
                // The combined B2C policy signs the user in as part of sign-up, so the sign-up popup
                // must also trigger the user fetch that transitions the app into the logged-in state.
                case 'signup-success':
                    LoggerService.debug(`[Auth:User] ${event} received, fetching the user`);
                    getUserWithRoles();
                    break;
                // case MsalEventTypeEnum.UnauthorizedEvent:
                case 'logout-success':
                // A failed logout does not navigate away, so the app stays mounted and the user
                // would otherwise survive in memory - and ProtectedRoute gates on exactly that.
                case 'logout-failure':
                    LoggerService.debug(`[Auth:User] ${event} received, clearing the user`);
                    cleanupUser();
                    break;
            }
        });

        return () => {
            authSub.current?.unsubscribe();
            authSub.current = null;
        };
    }, [authEvents]);

    return useMemo(
        () => ({
            user,
            hasPermission,
            hasAnyPermission,
            hasAllPermissions,
            orgHasPermission,
            hasAnyPermissionForOrg,
            hasAnyPermissionForAnyOrg,
            userHasPermission,
            userHasAnyPermission,
            userOrgHasPermission,
            userHasAnyPermissionForOrg,
            userHasAnyPermissionForAnyOrg
        }),
        [
            user,
            hasPermission,
            hasAnyPermission,
            hasAllPermissions,
            orgHasPermission,
            hasAnyPermissionForOrg,
            hasAnyPermissionForAnyOrg,
            userHasPermission,
            userHasAnyPermission,
            userOrgHasPermission,
            userHasAnyPermissionForOrg,
            userHasAnyPermissionForAnyOrg
        ]
    );
};
